import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, SearchIcon, TrendingUpIcon, AwardIcon, UsersIcon, ZapIcon, DatabaseIcon } from 'lucide-react';
const WindowFrame = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`rounded-xl border border-neutral-200 bg-white shadow-sm overflow-hidden flex flex-col h-full ${className}`} data-id="element-85">
    <div className="h-8 border-b border-neutral-100 bg-neutral-50 flex items-center px-3 gap-1.5 shrink-0" data-id="element-86">
      <div className="w-2.5 h-2.5 rounded-full bg-neutral-200" data-id="element-87" />
      <div className="w-2.5 h-2.5 rounded-full bg-neutral-200" data-id="element-88" />
      <div className="w-2.5 h-2.5 rounded-full bg-neutral-200" data-id="element-89" />
    </div>
    <div className="flex-1 p-4 relative overflow-hidden" data-id="element-90">{children}</div>
  </div>;
export function DashboardMock() {
  return <WindowFrame data-id="element-91">
      <div className="flex gap-4 h-full" data-id="element-92">
        <div className="w-16 shrink-0 flex flex-col gap-3 border-r border-neutral-100 pr-4" data-id="element-93">
          <div className="h-4 w-full bg-neutral-100 rounded" data-id="element-94" />
          <div className="h-4 w-3/4 bg-neutral-100 rounded" data-id="element-95" />
          <div className="h-4 w-5/6 bg-neutral-100 rounded" data-id="element-96" />
          <div className="h-4 w-full bg-neutral-100 rounded mt-auto" data-id="element-97" />
        </div>
        <div className="flex-1 flex flex-col gap-4" data-id="element-98">
          <div className="flex justify-between items-end" data-id="element-99">
            <div data-id="element-100">
              <div className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider mb-1" data-id="element-101">
                Total Usage
              </div>
              <div className="text-2xl font-serif" data-id="element-102">24,592</div>
            </div>
            <BarChart3Icon className="w-5 h-5 text-accent" data-id="element-103" />
          </div>
          <div className="flex-1 flex items-end gap-2 mt-4" data-id="element-104">
            {[40, 25, 60, 45, 80, 55, 90].map((h, i) => <motion.div key={i} className="flex-1 bg-accent/10 rounded-t-sm relative group" initial={{
            height: 0
          }} whileInView={{
            height: `${h}%`
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} viewport={{
            once: true
          }} data-id="element-105">
                <div className="absolute bottom-0 left-0 right-0 bg-accent rounded-t-sm transition-all duration-300" style={{
              height: '100%'
            }} data-id="element-106" />
              </motion.div>)}
          </div>
        </div>
      </div>
    </WindowFrame>;
}
export function SkillsLibraryMock() {
  const skills = [{
    icon: <UsersIcon className="w-3 h-3" data-id="element-107" />,
    name: 'Lead Gen'
  }, {
    icon: <ZapIcon className="w-3 h-3" data-id="element-108" />,
    name: 'Data Parse'
  }, {
    icon: <DatabaseIcon className="w-3 h-3" data-id="element-109" />,
    name: 'CRM Sync'
  }, {
    icon: <SearchIcon className="w-3 h-3" data-id="element-110" />,
    name: 'Research'
  }];
  return <WindowFrame data-id="element-111">
      <div className="flex flex-col h-full gap-3" data-id="element-112">
        <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-100 rounded-md px-2 py-1.5" data-id="element-113">
          <SearchIcon className="w-3 h-3 text-neutral-400" data-id="element-114" />
          <div className="h-2 w-24 bg-neutral-200 rounded-full" data-id="element-115" />
        </div>
        <div className="grid grid-cols-2 gap-2 flex-1" data-id="element-116">
          {skills.map((skill, i) => <motion.div key={i} className="border border-neutral-100 rounded-md p-2 flex flex-col gap-2 bg-white" initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} viewport={{
          once: true
        }} data-id="element-117">
              <div className="w-6 h-6 rounded bg-accent/10 text-accent flex items-center justify-center" data-id="element-118">
                {skill.icon}
              </div>
              <div className="text-[10px] font-medium text-neutral-600" data-id="element-119">
                {skill.name}
              </div>
            </motion.div>)}
        </div>
      </div>
    </WindowFrame>;
}
export function EfficiencyChartMock() {
  return <WindowFrame data-id="element-120">
      <div className="flex flex-col h-full justify-between" data-id="element-121">
        <div className="flex justify-between items-start" data-id="element-122">
          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent" data-id="element-123">
            <TrendingUpIcon className="w-4 h-4" data-id="element-124" />
          </div>
          <div className="text-right" data-id="element-125">
            <div className="text-[10px] text-neutral-400 font-medium uppercase tracking-wider mb-1" data-id="element-126">
              Efficiency Gain
            </div>
            <div className="text-2xl font-serif text-accent" data-id="element-127">+32%</div>
          </div>
        </div>

        <div className="relative h-24 mt-4 w-full" data-id="element-128">
          <svg viewBox="0 0 100 40" className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-129">
            <motion.path d="M0,35 C20,35 30,25 50,20 C70,15 80,5 100,0" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent" initial={{
            pathLength: 0
          }} whileInView={{
            pathLength: 1
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} viewport={{
            once: true
          }} data-id="element-130" />
            <motion.path d="M0,35 C20,35 30,25 50,20 C70,15 80,5 100,0 L100,40 L0,40 Z" fill="currentColor" className="text-accent/10" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} viewport={{
            once: true
          }} data-id="element-131" />
          </svg>
        </div>
      </div>
    </WindowFrame>;
}
export function CertificateMock() {
  return <WindowFrame data-id="element-132">
      <div className="flex flex-col items-center justify-center h-full text-center relative" data-id="element-133">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,theme(colors.accent.DEFAULT/0.05)_0%,transparent_70%)]" data-id="element-134" />
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        bounce: 0.5
      }} viewport={{
        once: true
      }} className="relative z-10" data-id="element-135">
          <div className="w-12 h-12 mx-auto rounded-full bg-accent text-white flex items-center justify-center mb-3 shadow-lg shadow-accent/20" data-id="element-136">
            <AwardIcon className="w-6 h-6" data-id="element-137" />
          </div>
          <div className="text-xs font-serif font-medium text-neutral-800 mb-1" data-id="element-138">
            AI Certified Professional
          </div>
          <div className="h-1 w-12 bg-neutral-200 mx-auto rounded-full mb-2" data-id="element-139" />
          <div className="text-[9px] text-neutral-400 uppercase tracking-widest" data-id="element-140">
            Anthropic Claude
          </div>
        </motion.div>
      </div>
    </WindowFrame>;
}